import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PenLine } from "lucide-react";
import SiteNavbar from "../components/SiteNavbar";
import SiteFooter from "../components/SiteFooter";
import BookBlogCard from "../components/BookBlogCard";
import { BOOK_BLOG_POSTS } from "../data/bookBlogContent";

const ease = [0.21, 0.47, 0.32, 0.98];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.75, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const BookBlogPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="book-blog-page"
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-32 md:pt-40 pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease }}
            className="text-center max-w-3xl mx-auto"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              The PagePeers journal
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
              style={{ color: "var(--text)" }}
            >
              Book{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                blog
              </span>
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: "var(--text-soft)" }}
            >
              Reviews, shelf diaries, circle roundups, and vlog notes from the
              PagePeers reading room — unhurried stories about how we actually read.
            </p>
          </motion.div>

          <div className="mt-16 md:mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOOK_BLOG_POSTS.map((post, i) => (
              <FadeIn key={post.slug} delay={i * 0.06}>
                <BookBlogCard post={post} testId={`book-blog-card-${i}`} />
              </FadeIn>
            ))}
          </div>

          <FadeIn className="mt-20 md:mt-28">
            <div
              className="rounded-3xl p-10 md:p-14 text-center"
              style={{
                background:
                  "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
                border: "1px solid var(--border)",
              }}
            >
              <PenLine
                className="w-8 h-8 mx-auto mb-5"
                style={{ color: "var(--brand)" }}
              />
              <h2
                className="font-serif text-3xl md:text-4xl font-medium mb-4"
                style={{ color: "var(--text)" }}
              >
                Share your reading story
              </h2>
              <p
                className="text-base mb-8 max-w-lg mx-auto"
                style={{ color: "var(--text-soft)" }}
              >
                Join PagePeers to post highlights, host a circle, and see your
                shelf featured in future journal roundups.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://club.pagepeers.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-transform hover:scale-[1.02]"
                  style={{
                    background: "var(--brand)",
                    color: "var(--on-brand)",
                    boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
                  }}
                >
                  Join the Club
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/reading-guides"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors"
                  style={{
                    borderColor: "var(--border)",
                    color: "var(--text)",
                    background: "var(--surface)",
                  }}
                >
                  Reading guides
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default BookBlogPage;
